import type { StorefrontBrand, StorefrontCatalog, StorefrontCategoryNode } from '@erp/shared';
import { Loader2, Search, Tag, X } from 'lucide-react';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { formatMoney } from '../../lib/money.js';
import { publicApi } from '../../lib/publicApi.js';

import { pricingRecorder } from './buyerPricing.js';
import { OriginalPrice, resolveSaving } from './discountDisplay.js';
import { StoreImage } from './StoreImage.js';

/**
 * Global storefront search as a header BUTTON that opens a command-palette-style
 * modal: live product search (debounced) + client-side category/brand filtering.
 * Enter / "see all" jumps to the shop grid. No external dependency.
 */
export function StorefrontSearchModal(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug } = useStorefront();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<StorefrontCatalog['items']>([]);
  const [cats, setCats] = useState<StorefrontCategoryNode[]>([]);
  const [brands, setBrands] = useState<StorefrontBrand[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load category + brand lists once the palette is first opened.
  useEffect(() => {
    if (!open) return;
    void publicApi().get<{ items: StorefrontCategoryNode[] }>(`/${slug}/categories`).then((r) => setCats(r.data.items)).catch(() => undefined);
    void publicApi().get<{ items: StorefrontBrand[] }>(`/${slug}/brands`).then((r) => setBrands(r.data.items)).catch(() => undefined);
  }, [open, slug, i18n.language]);

  // Live product search (debounced) while the palette is open.
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setProducts([]); setLoading(false); return undefined; }
    setLoading(true);
    const id = setTimeout(() => {
      const remember = pricingRecorder(slug);
      void publicApi()
        .get<StorefrontCatalog>(`/${slug}/products`, { params: { q: term, limit: 6 } })
        .then((r) => { setProducts(r.data.items); remember(r.data.pricing); })
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [q, open, slug, i18n.language]);

  // While open: focus the input, lock body scroll, ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tmr = setTimeout(() => inputRef.current?.focus(), 40);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; clearTimeout(tmr); };
  }, [open]);

  const close = (): void => { setOpen(false); setQ(''); setProducts([]); };
  const term = q.trim().toLowerCase();
  const matchCats = term ? cats.filter((c) => c.name.toLowerCase().includes(term)).slice(0, 5) : [];
  const matchBrands = term ? brands.filter((b) => b.name.toLowerCase().includes(term)).slice(0, 5) : [];
  // Each result carries the currency the SERVER priced it in (a signed-in
  // customer's group currency, else the store's) — never assume the store's.
  const money = (v: string, currency: string): string => formatMoney(v, { locale: i18n.language, currency });
  const hasResults = products.length > 0 || matchCats.length > 0 || matchBrands.length > 0;
  const go = (to: string): void => { close(); navigate(to); };
  const submit = (): void => { const tq = q.trim(); if (tq) go(`/s/${slug}/shop?q=${encodeURIComponent(tq)}`); };
  const sectionLabel = 'px-1 pb-1 text-[0.7rem] font-bold uppercase tracking-wider text-brand/80';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('store.search.label', { defaultValue: 'Search the store' })}
        title={t('store.search.label', { defaultValue: 'Search the store' })}
        className="inline-flex items-center rounded-full border border-border bg-bg p-2.5 text-fg shadow-sm transition-[transform,border-color,color] duration-150 ease-out hover:border-brand/40 active:scale-[0.95] motion-reduce:transform-none"
      >
        <Search className="h-4 w-4" aria-hidden />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-950/70 px-4 pt-[10vh] backdrop-blur-md duration-200 animate-in fade-in-0"
          role="dialog"
          aria-modal="true"
          aria-label={t('store.search.label', { defaultValue: 'Search the store' })}
          onClick={close}
        >
          <div
            className="w-full max-w-2xl overflow-hidden rounded-2xl border border-brand/30 bg-bg shadow-2xl shadow-brand/10 ring-1 ring-brand/20 duration-200 animate-in fade-in-0 zoom-in-95 slide-in-from-top-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search input row — brand-tinted to draw focus */}
            <div className="relative flex items-center border-b border-border bg-gradient-to-b from-brand/[0.06] to-transparent">
              <Search className="pointer-events-none absolute inset-y-0 start-5 my-auto h-5 w-5 text-brand" aria-hidden />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder={t('store.search.placeholder', { defaultValue: 'Search products, categories, brands…' })}
                aria-label={t('store.search.label', { defaultValue: 'Search the store' })}
                className="h-16 w-full bg-transparent ps-14 pe-24 text-lg outline-none placeholder:text-fg-subtle"
              />
              <div className="absolute inset-y-0 end-3 my-auto flex items-center gap-1.5">
                {q && (
                  <button type="button" onClick={() => { setQ(''); setProducts([]); inputRef.current?.focus(); }} className="grid h-7 w-7 place-items-center rounded-full text-fg-muted transition hover:bg-bg-subtle hover:text-fg" aria-label={t('store.shop.clear', { defaultValue: 'Clear' })}>
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                )}
                <button type="button" onClick={close} aria-label={t('store.product.close', { defaultValue: 'Close' })} className="rounded-md border border-border bg-bg-subtle px-2 py-1 text-[0.65rem] font-semibold text-fg-muted transition hover:text-fg">Esc</button>
              </div>
            </div>

            <div className="max-h-[58vh] overflow-y-auto p-2">
              {q.trim().length < 2 ? (
                <div className="px-3 py-10 text-center text-sm text-fg-muted">{t('store.search.hint', { defaultValue: 'Type to search the whole store…' })}</div>
              ) : loading && !hasResults ? (
                <div className="flex items-center justify-center gap-2 px-3 py-10 text-sm text-fg-muted"><Loader2 className="h-4 w-4 animate-spin text-brand" aria-hidden />{t('store.search.searching', { defaultValue: 'Searching…' })}</div>
              ) : !hasResults ? (
                <div className="px-3 py-10 text-center text-sm text-fg-muted">{t('store.search.empty', { defaultValue: 'No matches found.' })}</div>
              ) : (
                <>
                  {matchCats.length > 0 && (
                    <div className="px-2 pb-1 pt-2">
                      <div className={sectionLabel}>{t('store.categories.title', { defaultValue: 'Categories' })}</div>
                      {matchCats.map((c) => (
                        <button key={c.id} type="button" onClick={() => go(`/s/${slug}/category/${c.slug}`)} className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-start text-sm transition hover:bg-brand/5">
                          <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-brand/10 text-brand"><Tag className="h-4 w-4" aria-hidden /></span>
                          <span className="truncate">{c.name}</span>
                          <span className="ms-auto text-xs text-fg-subtle">{t('store.category.count', { count: c.productCount })}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {matchBrands.length > 0 && (
                    <div className="px-2 pb-1 pt-2">
                      <div className={sectionLabel}>{t('store.brands.title', { defaultValue: 'Brands' })}</div>
                      {matchBrands.map((b) => (
                        <button key={b.id} type="button" onClick={() => go(`/s/${slug}/brand/${b.id}`)} className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-start text-sm transition hover:bg-brand/5">
                          <span className="grid h-8 w-8 flex-none place-items-center overflow-hidden rounded-lg bg-white ring-1 ring-border">
                            <StoreImage src={b.imageUrl} alt="" className="h-full w-full object-contain p-0.5" />
                          </span>
                          <span className="truncate">{b.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {products.length > 0 && (
                    <div className="px-2 pb-1 pt-2">
                      <div className={sectionLabel}>{t('store.shop.title', { defaultValue: 'Products' })}</div>
                      {products.map((p, i) => {
                        // Discounted results read as a strikethrough pair — the
                        // percent badge stays a product-card idiom.
                        const saving = resolveSaving(p);
                        return (
                          <Link key={p.id} to={`/s/${slug}/product/${p.slug ?? p.id}`} onClick={close} style={{ ['--sf-delay']: `${i * 35}ms` } as CSSProperties} className="sf-card flex items-center gap-3 rounded-xl px-2.5 py-2 transition hover:bg-brand/5">
                            <span className="grid h-12 w-12 flex-none place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
                              <StoreImage src={p.imageUrl} alt="" className="h-full w-full object-contain" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium">{p.name}</span>
                              <span className="block text-xs text-fg-muted">
                                <span className="font-mono font-semibold tabular-nums text-fg">{money(p.price, p.currencyCode)}</span>
                                {saving && <OriginalPrice amount={saving.originalPrice} currency={p.currencyCode} className="ms-1.5 font-mono tabular-nums" />}
                                {p.unit ? <span className="text-fg-subtle"> / {p.unit}</span> : ''}
                              </span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer — keyboard hints + see-all action */}
            {q.trim().length >= 2 && hasResults && (
              <div className="flex items-center justify-between gap-2 border-t border-border bg-bg-subtle/50 px-3 py-2">
                <span className="hidden items-center gap-1.5 text-xs text-fg-muted sm:inline-flex">
                  <kbd className="rounded border border-border bg-bg px-1.5 py-0.5 font-sans">↵</kbd>
                  {t('store.search.enterHint', { defaultValue: 'See all results' })}
                </span>
                <button type="button" onClick={submit} className="ms-auto inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-sm font-semibold text-brand-contrast shadow-sm transition hover:opacity-90 active:scale-[0.98]">
                  <Search className="h-3.5 w-3.5" aria-hidden />
                  {t('store.search.seeAll', { defaultValue: 'See all results for "{{q}}"', q: q.trim() })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
